import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loanApplicationApi } from '../api/loanApplicationApi';
import { appToast } from '../../../../components/AppToast';

export function useLoanApplicationEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const mode = searchParams.get('mode') || (editId ? 'edit' : 'new');
  const isView = mode === 'view';

  const employeeInfo = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: stored.name || stored.userName || 'Divyanshu Singh',
        empId: stored.employee_id || stored.empCode || '348414',
      };
    } catch {
      return {
        name: 'Divyanshu Singh',
        empId: '348414',
      };
    }
  }, []);

  const [applyDate, setApplyDate] = useState('31/08/2026');
  const [payHead, setPayHead] = useState('');
  const [employee, setEmployee] = useState(employeeInfo.name);
  const [loanDate, setLoanDate] = useState('2026-08-31');
  const [loanType, setLoanType] = useState('');
  const [loanAmount, setLoanAmount] = useState('0');
  const [emiFromDate, setEmiFromDate] = useState('2026-09-01');
  const [emiAmount, setEmiAmount] = useState('0');
  const [remarks, setRemarks] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const payHeadOptions = [
    { value: '', label: 'Select' },
    { value: 'Salary Advance', label: 'Salary Advance' },
    { value: 'Personal Loan', label: 'Personal Loan' },
    { value: 'Festival Advance', label: 'Festival Advance' },
    { value: 'Emergency Loan', label: 'Emergency Loan' },
  ];

  const loanTypeOptions = [
    { value: '', label: 'Select' },
    { value: 'Short Term Loan', label: 'Short Term Loan' },
    { value: 'Long Term Loan', label: 'Long Term Loan' },
    { value: 'Emergency Advance', label: 'Emergency Advance' },
  ];

  // Auto calculate suggested EMI if loan amount changes
  const handleLoanAmountChange = (val) => {
    setLoanAmount(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      setEmiAmount(String(Math.round(num / 10)));
    }
  };

  // Load Record if Editing/Viewing
  useEffect(() => {
    async function init() {
      if (editId) {
        try {
          const res = await loanApplicationApi.getById(editId);
          if (res.data) {
            const r = res.data;
            if (r.applyDate) setApplyDate(r.applyDate);
            if (r.payHead) setPayHead(r.payHead);
            if (r.name) setEmployee(r.name);
            if (r.loanDate) setLoanDate(r.loanDate);
            if (r.loanType) setLoanType(r.loanType);
            if (r.loanAmount) setLoanAmount(r.loanAmount);
            if (r.emiFromDate) setEmiFromDate(r.emiFromDate);
            if (r.emiAmount) setEmiAmount(r.emiAmount);
            if (r.remarks) setRemarks(r.remarks);
          }
        } catch (err) {
          console.error('Failed to load loan details', err);
        }
      }
    }
    init();
  }, [editId]);

  const validate = () => {
    if (!payHead) {
      appToast.error('Please select a Pay Head');
      return false;
    }
    if (!loanDate) {
      appToast.error('Please select Loan Date');
      return false;
    }
    if (!loanType) {
      appToast.error('Please select Loan Type');
      return false;
    }
    if (!loanAmount || parseFloat(loanAmount) <= 0) {
      appToast.error('Please enter a valid Loan Amount');
      return false;
    }
    if (!emiFromDate) {
      appToast.error('Please select EMI From Date');
      return false;
    }
    if (!emiAmount || parseFloat(emiAmount) <= 0) {
      appToast.error('Please enter a valid EMI Amount');
      return false;
    }
    if (!remarks.trim()) {
      appToast.error('Please enter Remarks / Justification');
      return false;
    }
    return true;
  };

  const handleSaveAndExit = async (e) => {
    if (e) e.preventDefault();
    if (isView) return;
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name: employee,
        empId: employeeInfo.empId,
        applyDate,
        payHead,
        loanDate,
        loanType,
        loanAmount: parseFloat(loanAmount).toFixed(2),
        emiFromDate,
        emiAmount: parseFloat(emiAmount).toFixed(2),
        remarks,
        status: 'Pending',
      };

      if (editId) {
        await loanApplicationApi.update(editId, payload);
        appToast.success('Loan application updated successfully');
      } else {
        await loanApplicationApi.create(payload);
        appToast.success('Loan application submitted successfully');
      }
      navigate('/loan-application');
    } catch {
      appToast.error('Failed to submit loan application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async (e) => {
    if (e) e.preventDefault();
    if (isView) return;
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name: employee,
        empId: employeeInfo.empId,
        applyDate,
        payHead,
        loanDate,
        loanType,
        loanAmount: parseFloat(loanAmount).toFixed(2),
        emiFromDate,
        emiAmount: parseFloat(emiAmount).toFixed(2),
        remarks,
        status: 'Pending',
      };

      if (editId) {
        await loanApplicationApi.update(editId, payload);
        appToast.success('Loan application saved');
      } else {
        await loanApplicationApi.create(payload);
        appToast.success('Loan application submitted');
        setPayHead('');
        setLoanType('');
        setLoanAmount('0');
        setEmiAmount('0');
        setRemarks('');
      }
    } catch {
      appToast.error('Failed to save loan application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/loan-application');
  };

  return {
    state: {
      isEdit: Boolean(editId) && !isView,
      isView,
      mode,
      editId,
      applyDate,
      payHead,
      employee,
      loanDate,
      loanType,
      loanAmount,
      emiFromDate,
      emiAmount,
      remarks,
      isSaving,
      payHeadOptions,
      loanTypeOptions,
    },
    actions: {
      setPayHead,
      setLoanDate,
      setLoanType,
      setLoanAmount: handleLoanAmountChange,
      setEmiFromDate,
      setEmiAmount,
      setRemarks,
      handleSaveAndExit,
      handleSaveAndContinue,
      handleCancel,
    },
  };
}
